import React from 'react';
import { useGate } from 'statsig-react';
import type { Address } from 'viem';
import { useStableCallback } from '@soundxyz/graphql-react-query/utils';
import { SUPER_MINTER_ADDRESS } from '@soundxyz/tiered-sdk-viem/abi/super-minter';
import type { PayWithCCSource } from '@soundxyz/utils/analytics';
import { getChainName, NULL_ADDRESS } from '@soundxyz/utils/constants/constants';
import { ERROR_TYPE, MINTING_ERROR_ACTIONS, PILLARS } from '@soundxyz/utils/constants/logging';
import type { ButtonProps } from '@components/main-components/button/Button';
import { Button } from '@components/main-components/button/Button';
import { useAuth } from '@context/auth/base';
import { useConnectedAuth } from '@context/auth/connected';
import { useMintReferral } from '@context/mintReferral';
import { ModalContext, ModalType } from '@context/modal';
import { ToastContext, useToast } from '@context/toast';
import { useWalletRequest } from '@context/wallet-request';
import { PayEditionWithEthDocument, useQuery } from '@graphql/reactQuery';
import { useCheckReferrableRelease } from '@hooks/useCheckReferrable';
import { useLegacyEthers } from '@hooks/useLegacyEthers';
import { useLogError } from '@hooks/useLogErrorNew';
import { trackClickEvent } from '@lib/analytics';
import { type SuperMinterScheduleWithTier, useTieredMintPrice } from '@store/sdk/tieredReactQuery';
import { AVAILABLE_CHAIN_IDS, FEATURE_GATES } from '@utils/constants';
import { EthWhiteLogo } from '../../InsufficientFundsModal';
import { PaperEthMintModal } from '../CreditCard/Paper/EthMintModal';
import { usePaperTierCheckout } from '../CreditCard/Paper/usePaperTierCheckout';

function CheckoutWithETH({
  onClick,
  disabled,
  loading,
  className,
  variant,
}: {
  className?: string;
  onClick: () => void;
  disabled: boolean;
  loading: boolean;
  variant: ButtonProps['variant'];
}) {
  const { requestRejected } = useWalletRequest();
  const { isSwitchingNetwork, expectMatchingChain } = useConnectedAuth();

  const { openToast } = useToast();

  const [label, setLabel] = React.useState<
    'Pay with ETH on Mainnet' | 'Continue' | 'Switch chain in wallet'
  >('Pay with ETH on Mainnet');

  React.useEffect(() => {
    if (requestRejected) {
      setLabel('Pay with ETH on Mainnet');
    }
  }, [requestRejected]);

  const handleClick = useStableCallback(async () => {
    const chain = expectMatchingChain(AVAILABLE_CHAIN_IDS.eth, {
      onSyncSwitchSuccess() {
        openToast({
          text: 'Successfully switched to Ethereum.\nTap Pay with ETH on Mainnet to continue.',
          variant: 'success',
        });
      },
    });

    if (chain.stop) return;

    if (chain.switching) {
      try {
        setLabel('Switch chain in wallet');
        await chain.switching;
      } catch (err) {
        setLabel('Pay with ETH on Mainnet');

        return;
      }
    }

    setLabel('Continue');

    onClick();
  });

  return (
    <Button
      disabled={disabled}
      loading={loading || isSwitchingNetwork}
      label={label}
      trailingIcon={<EthWhiteLogo />}
      variant={variant}
      size="XL"
      fullWidth
      className={className}
      onClick={handleClick}
    />
  );
}

export function PayTierEditionWithEth({
  releaseId,
  editionAddress,
  activeSchedule,
  chainId,
  quantity,
  canCollectSafely,
  disabled,
  className,
  variant,
  source,
  giftTo,
}: {
  releaseId: string;
  editionAddress: string;
  activeSchedule: SuperMinterScheduleWithTier;
  chainId: number;
  quantity: number;
  canCollectSafely: boolean;
  disabled: boolean;
  className?: string;
  variant: ButtonProps['variant'];
  source: PayWithCCSource;
  giftTo: Address | null;
}) {
  const logError = useLogError();
  const { value: isEthCheckoutEnabled } = useGate(FEATURE_GATES.PAPER_ETH_CHECKOUT);
  const { getLegacySigner } = useLegacyEthers();
  const { totalMintPriceWei } = useTieredMintPrice({
    chainId,
    editionAddress,
    mintSchedule: activeSchedule,
    quantity,
  });

  const totalMintPrice = totalMintPriceWei?.total;

  const { getPaperKey, isLoading: creditCardButtonIsLoading } = usePaperTierCheckout({
    releaseId,
    buyPriceWei: totalMintPrice || 0n,
    chain: getChainName(chainId),
    editionAddress,
    scheduleNumber: activeSchedule.scheduleNum,
    tierNumber: activeSchedule.tier,
    quantity,
    type: 'ETH_MAINNET',
    giftTo: giftTo,
  });

  const { openModal } = ModalContext.useContainer();
  const { openToast } = ToastContext.useContainer();

  const { data } = useQuery(PayEditionWithEthDocument, {
    variables: {
      releaseId,
    },
    staleTime: 0,
  });

  const release = data?.data.release;

  const { getAffiliate } = useCheckReferrableRelease({
    release,
  });

  const showButton = isEthCheckoutEnabled;
  const creditCardLoading = !canCollectSafely || creditCardButtonIsLoading;
  const { referral } = useMintReferral();
  const affiliate = getAffiliate(referral) || NULL_ADDRESS;

  const { authUserAddress } = useAuth();
  const toAddress = giftTo ?? authUserAddress ?? '';

  const handleClick = useStableCallback(async () => {
    if (!release) return;

    const [signer, paperKey] = await Promise.all([
      getLegacySigner(AVAILABLE_CHAIN_IDS.eth),
      getPaperKey({ affiliate }),
    ]);

    if ('key' in paperKey) {
      const affiliateAddress = affiliate.toLowerCase();
      trackClickEvent({
        event: 'Pay with ETH on Mainnet Clicked',
        properties: {
          associationId: releaseId,
          associationType: 'Release',
          chainId,
          quantity,
          affiliateAddress,
          minterAddress: SUPER_MINTER_ADDRESS,
          sourceComponent: source,
          mintTo: toAddress,
        },
      });

      openModal(ModalType.PAPER_ETH_MINT_MODAL, {
        body: (
          <PaperEthMintModal
            selectedTier={activeSchedule.tier}
            paperKey={paperKey.key}
            signer={signer}
            data={release}
            releaseId={releaseId}
            chainId={chainId}
            onSuccessfulTransaction={() => {
              trackClickEvent({
                event: 'Pay with ETH on Mainnet Success',
                properties: {
                  associationId: releaseId,
                  associationType: 'Release',
                  chainId,
                  quantity,
                  affiliateAddress,
                  minterAddress: SUPER_MINTER_ADDRESS,
                  sourceComponent: source,
                  mintTo: toAddress,
                  isGifted: !!giftTo,
                },
              });
            }}
          />
        ),
      });
    } else if ('error' in paperKey) {
      openToast({
        text: paperKey.error,
        variant: 'error',
      });
    } else {
      logError({
        action: MINTING_ERROR_ACTIONS.PAPER_ERROR,
        error: new Error('Error fetching paper key for PayEditionWithEth'),
        indexedTags: {
          chainId,
          releaseId,
        },
        errorType: ERROR_TYPE.PAPER_CC,
        pillar: PILLARS.MINTING,
        isArtistOnlyPage: false,
        level: 'error',
        toast: 'Something really went wrong. Please try again.',
        message: 'Error fetching paper key for PayEditionWithEth',
      });
    }
  });

  // we just hide the button if we do not allow CC
  if (!showButton) {
    return null;
  }

  return (
    <CheckoutWithETH
      disabled={disabled || !release}
      loading={creditCardLoading}
      className={className}
      variant={variant}
      onClick={handleClick}
    />
  );
}
